/**
 * Symmetric band bar centred on the booked price.
 *
 * The rating behind it is an absolute distance (see `absolute_distance_percentage`
 * on the booked-price endpoint), so a quote that far off could sit on either side
 * of the booked price. Both mirrored segments of the carrier's band are highlighted
 * for that reason: the bar shows how far off the quote was without claiming a
 * direction the data does not carry.
 */
import { Box, Stack, Typography } from "@mui/material";

import { CompetitivenessTier, TIER_CONFIG } from "./tiers";

const BAND_TIERS: CompetitivenessTier[] = [
  CompetitivenessTier.NOT_COMPETITIVE,
  CompetitivenessTier.IN_THE_BALLPARK,
  CompetitivenessTier.COMPETITIVE,
  CompetitivenessTier.HIGHLY_COMPETITIVE,
  CompetitivenessTier.COMPETITIVE,
  CompetitivenessTier.IN_THE_BALLPARK,
  CompetitivenessTier.NOT_COMPETITIVE,
];

const CENTRE_INDEX = BAND_TIERS.indexOf(CompetitivenessTier.HIGHLY_COMPETITIVE);

interface CompetitivenessBandBarProps {
  tier: CompetitivenessTier;
}

export function CompetitivenessBandBar(props: CompetitivenessBandBarProps) {
  const { tier } = props;
  const activeColor = TIER_CONFIG[tier].color;

  return (
    // the sentence under the bar already states the rating, so the bar itself is
    // decorative to a screen reader
    <Stack spacing={0.5} aria-hidden="true">
      <Stack direction="row" sx={{ width: "100%" }}>
        {BAND_TIERS.map((bandTier, index) => {
          const isActive = bandTier === tier;
          const isCentre = index === CENTRE_INDEX;
          return (
            <Box
              // bands mirror across the booked price, so a tier appears twice and
              // position is the only stable identity
              // eslint-disable-next-line react/no-array-index-key
              key={index}
              data-testid={`band-segment-${index}`}
              data-active={isActive}
              sx={{
                flex: 1,
                py: 0.5,
                textAlign: "center",
                bgcolor: isActive ? `${activeColor}20` : "grey.100",
                color: isActive ? activeColor : "text.disabled",
                fontSize: { xs: 0, sm: "11px" },
                minHeight: "24px",
                fontWeight: isActive ? 600 : 400,
                borderTop: "1px solid",
                borderBottom: "1px solid",
                borderLeft: isCentre ? "2px solid" : "1px solid",
                borderRight: isCentre ? "2px solid" : "1px solid",
                borderColor: isActive ? `${activeColor}50` : "grey.300",
                whiteSpace: "nowrap",
                overflow: "hidden",
              }}
            >
              {TIER_CONFIG[bandTier].bandLabel}
            </Box>
          );
        })}
      </Stack>

      <Stack direction="row" justifyContent="space-between">
        <Typography variant="caption" sx={{ color: "text.secondary" }}>
          Below booked price
        </Typography>
        <Typography variant="caption" sx={{ color: "text.secondary" }}>
          Above booked price
        </Typography>
      </Stack>
    </Stack>
  );
}
